import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { parseAsInteger, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import React from 'react'
import { RiArrowRightLine, RiHourglassFill } from 'react-icons/ri'
import { useErc20TokenContractAddressesQuery } from 'apollo/generated/graphqlClient'
import { CopyToClipboardButton } from 'components/CopyToClipboardButton'
import { ExternalLink } from 'components/ExternalLink'
import { InfoPopup } from 'components/InfoPopup'
import { Label } from 'components/Label'
import { Link } from 'components/Link'
import { Seo } from 'components/Seo'
import { Table } from 'components/Table'
import {
  NOT_APPLICABLE,
  PAGE_LENGTH,
  TIMESTAMP_COLUMN_FORMAT,
  TRUNCATED_MOBILE_TEXT_WIDTH,
  TXN_STATUS_ICONS,
  TXN_STATUS_VARIANTS,
} from 'constants/common'
import { Routes } from 'constants/routes'
import { env } from 'env.client'
import {
  TransactionListItemStatus as TxItemStatus,
  useGetTransactions,
  FilterTxTypeParamParameter,
} from 'generated/reactQueryClient'
import type { TransactionListItem as TxItem } from 'generated/reactQueryClient'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { useTransactionHashTruncateOptions } from 'hooks/useTransactionHashTruncateOptions'
import { useZircuitRPCGetTransactions } from 'hooks/useZircuitRPCGetTransactions'
import { DetailLayout } from 'layouts/DetailLayout/DetailLayout'
import {
  getAssetSymbol,
  getFormattedAsset,
  getFormattedNumber,
  getTruncatedText,
  weiToEth,
} from 'utils/common'
import {
  getTimestampColumnFormatValue,
  getTimestampColumnTooltipFormatValue,
  getTimestampColumnTooltipText,
} from 'utils/dayjs'
import { getImageSrc } from 'utils/getImageSrc'
import { getTxTypeLabel } from 'utils/getTxTypeLabel'

const TransactionsPage = () => {
  const COLORS = useThemeColors()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()
  const [block] = useQueryState('block', parseAsInteger)
  const [bundle] = useQueryState('bundle', parseAsString)
  const [batch] = useQueryState('batch', parseAsString)
  const [txType] = useQueryState(
    'txType',
    parseAsStringEnum(Object.values(FilterTxTypeParamParameter))
  )

  const [isAgeTimestampFormat, setIsAgeTimestampFormat] = React.useState<boolean>(true)

  const filters = [
    block && { name: 'block', value: block, route: 'blockDetail' },
    bundle && { name: 'bundle', value: bundle, route: 'bundleDetail' },
    // TODO: Remove if batch filter won't be no longer required to be backwards compatible.
    batch && { name: 'bundle', value: batch, route: 'bundleDetail' },
  ].filter(Boolean)

  // Apply only one filter (block or bundle) from query params.
  const [filter] = filters

  const { data, isLoading } = useGetTransactions({
    limit: PAGE_LENGTH,
    txType: txType ?? FilterTxTypeParamParameter.L2,
    ...(cursor && { [direction]: cursor }),
    // TODO: Replace all `batch*` strings with `bundle*` after zircuit API supports it.
    ...(filter && { [filter.name === 'bundle' ? 'batch' : filter.name]: filter.value.toString() }),
  })

  const { data: rpcCallData = [], isLoading: areRPCCallDataLoading } = useZircuitRPCGetTransactions(
    {
      variables: {
        data: data?.data,
        cursor: cursor ?? '',
      },
    }
  )

  const { data: { erc20TokenContractAddresses } = {}, loading: areErc20TokenImagesLoading } =
    useErc20TokenContractAddressesQuery()

  const timestampFormat = isAgeTimestampFormat
    ? TIMESTAMP_COLUMN_FORMAT.AGE
    : TIMESTAMP_COLUMN_FORMAT.DATE_TIME

  return (
    <>
      <Seo title="Transactions" />
      <Table
        data={data?.data ?? []}
        hasNextPage={!!data?.paginationContext?.next}
        hasPreviousPage={!!data?.paginationContext?.previous}
        onNextPage={() => {
          setDirection('next')
          setCursor(data?.paginationContext?.next ?? '')
        }}
        onPreviousPage={() => {
          setDirection('previous')
          setCursor(data?.paginationContext?.previous ?? '')
        }}
        title="L2 Transactions"
        isLoading={isLoading || areErc20TokenImagesLoading || areRPCCallDataLoading}
        {...(filter && {
          additionalHeaderContent: (
            <Flex ml={1} alignItems="center" gap={1}>
              <Text textTransform="capitalize">For {filter.name}</Text>
              <Link
                href={Routes[filter.route as 'blockDetail' | 'bundleDetail']({
                  id: filter.value.toString(),
                })}
              >
                {filter.value}
              </Link>
              {txType && <Text>(txn type: {getTxTypeLabel(txType)})</Text>}
            </Flex>
          ),
        })}
        renderMobileRow={(row: TxItem) => {
          const hasDetailPage = rpcCallData?.some((tx) => tx?.hash === row.l2TxHash)
          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            (token) => token.symbol === getAssetSymbol(row.transfers?.[0]?.symbol)
          )

          return (
            <Table.MobileRow key={row.l2TxHash}>
              {({ isExpanded }) => (
                <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                  <Table.MobileRowCell label="Txn Hash:">
                    <Flex align="center" gap={1.5}>
                      {!hasDetailPage && (
                        <InfoPopup
                          title="Be aware:"
                          triggerElement={
                            <Box
                              boxSize={4}
                              color={COLORS.grey01}
                              onClick={(event) => {
                                event.stopPropagation()
                              }}
                            >
                              <RiHourglassFill color="inherit" size={16} />
                            </Box>
                          }
                        >
                          Transaction is being mined, detail might not be available yet.
                        </InfoPopup>
                      )}
                      <Link
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                        href={Routes.transactionDetail({
                          txHash: String(row.l2TxHash),
                          activeTab: 0,
                        })}
                        w={TRUNCATED_MOBILE_TEXT_WIDTH}
                        isTruncated
                      >
                        {row.l2TxHash}
                      </Link>
                    </Flex>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Block L2:" isVisible={isExpanded}>
                    <Link
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}
                    >
                      {row.l2BlockIndex}
                    </Link>
                  </Table.AnimatedMobileRowCell>
                  <Table.AnimatedMobileRowCell label="Block L1:" isVisible={isExpanded}>
                    {row.l1BlockIndex ? (
                      <ExternalLink
                        href={`${env.NEXT_PUBLIC_L1_URL}/block/${row.l1BlockIndex}`}
                        label={String(row.l1BlockIndex)}
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      />
                    ) : (
                      <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                    )}
                  </Table.AnimatedMobileRowCell>
                  <Table.AnimatedMobileRowCell
                    label={
                      data?.data?.some((tx) => tx.timestamp) ? (
                        <Table.InteractiveMobileHeaderCell
                          variant="text3regular"
                          onClick={() => {
                            setIsAgeTimestampFormat((prev) => !prev)
                          }}
                        >
                          {timestampFormat}:
                        </Table.InteractiveMobileHeaderCell>
                      ) : (
                        `${timestampFormat}:`
                      )
                    }
                    isVisible={isExpanded}
                  >
                    <InfoPopup
                      title={
                        timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                          ? 'Transaction Datetime:'
                          : 'Transaction Age:'
                      }
                      triggerElement={
                        <Text
                          variant="text3regular"
                          color={COLORS.grey03}
                          whiteSpace="nowrap"
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {row.timestamp
                            ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                            : NOT_APPLICABLE}
                        </Text>
                      }
                    >
                      {row.timestamp
                        ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </InfoPopup>
                  </Table.AnimatedMobileRowCell>
                  <Table.MobileRowCell label="From:">
                    <Link
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      href={Routes.addressDetail({ hash: String(row.from) })}
                    >
                      {getTruncatedText(row.from, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                    </Link>
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="To:">
                    <Link
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      href={Routes.addressDetail({ hash: row.to })}
                    >
                      {getTruncatedText(row.to, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                    </Link>
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="Txn Status:">
                    <Box>
                      {row.status === TxItemStatus.failed ? (
                        <InfoPopup
                          title="Txn Status:"
                          triggerElement={
                            <Label
                              variant={TXN_STATUS_VARIANTS[row.status]}
                              icon={TXN_STATUS_ICONS[row.status]}
                              onClick={(event) => {
                                event.stopPropagation()
                              }}
                            >
                              {row.status}
                            </Label>
                          }
                        >
                          {row.failureReason}
                        </InfoPopup>
                      ) : (
                        <Label
                          variant={TXN_STATUS_VARIANTS[row.status]}
                          icon={TXN_STATUS_ICONS[row.status]}
                        >
                          {row.status}
                        </Label>
                      )}
                    </Box>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Confirmed By:" isVisible={isExpanded}>
                    <Link
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      href={Routes.addressDetail({ hash: String(row.confirmedBy) })}
                    >
                      {getTruncatedText(row.confirmedBy, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                    </Link>
                  </Table.AnimatedMobileRowCell>
                  <Table.MobileRowCell label="Value:">
                    <Flex alignItems="center">
                      <Image
                        alt="Token Asset"
                        {...(row.transfers?.[0]?.symbol &&
                        erc20TokenContractAddresses?.length &&
                        erc20TokenAsset?.type
                          ? {
                              src: getImageSrc({
                                contractAddressL2: erc20TokenAsset?.contract_address_l2,
                                type: erc20TokenAsset.type,
                              }),
                              boxSize: 4,
                            }
                          : { src: '/assets/ethereum2.svg' })}
                      />
                      <Text variant="text3regular" ml={1}>
                        {getFormattedAsset({ transfers: row.transfers, value: row.value })}
                      </Text>
                    </Flex>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Txn Fee:" isVisible={isExpanded}>
                    <InfoPopup
                      title="Effective Txn Fee:"
                      triggerElement={
                        <Text
                          variant="text3regular"
                          color={COLORS.grey03}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {getFormattedNumber(weiToEth(row.fee))} ETH
                        </Text>
                      }
                    >
                      <Text
                        variant="text3regular"
                        color={COLORS.grey03}
                        w={TRUNCATED_MOBILE_TEXT_WIDTH}
                        isTruncated
                        whiteSpace="nowrap"
                      >
                        {getFormattedNumber(weiToEth(row.effectiveFee))} ETH
                      </Text>
                    </InfoPopup>
                  </Table.AnimatedMobileRowCell>
                </Flex>
              )}
            </Table.MobileRow>
          )
        }}
        renderTableHeader={() => (
          <>
            <Table.HeaderCell w={110}>Txn Hash</Table.HeaderCell>
            <Table.HeaderCell>Block L2</Table.HeaderCell>
            <Table.HeaderCell>Block L1</Table.HeaderCell>
            {data?.data?.some((tx) => tx.timestamp) ? (
              <Table.InteractiveHeaderCell
                tooltipText={getTimestampColumnTooltipText(timestampFormat)}
                onClick={() => {
                  setIsAgeTimestampFormat((prev) => !prev)
                }}
              >
                {timestampFormat}
              </Table.InteractiveHeaderCell>
            ) : (
              <Table.HeaderCell>{timestampFormat}</Table.HeaderCell>
            )}
            <Table.HeaderCell w={{ base: 'auto', '2xl': '13rem' }}>From</Table.HeaderCell>
            <Table.HeaderCell w={{ base: 'auto', '2xl': '6rem' }} />
            <Table.HeaderCell>To</Table.HeaderCell>
            <Table.HeaderCell>Txn Status</Table.HeaderCell>
            <Table.HeaderCell>Confirmed By</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
            <Table.HeaderCell>Txn Fee</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: TxItem) => {
          const hasDetailPage = rpcCallData?.some((tx) => tx?.hash === row.l2TxHash)
          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            ({ symbol }: { symbol: string }) =>
              symbol === getAssetSymbol(row.transfers?.[0]?.symbol)
          )

          return (
            <Table.Row key={row.l2TxHash}>
              <Table.RowCell>
                <Flex align="center" gap={1.5}>
                  {!hasDetailPage && (
                    <InfoPopup
                      title="Be aware:"
                      triggerElement={
                        <Box boxSize={4} color={COLORS.grey01}>
                          <RiHourglassFill color="inherit" size={16} />
                        </Box>
                      }
                    >
                      Transaction is being mined, detail might not be available yet.
                    </InfoPopup>
                  )}
                  <Flex align="center" gap={1}>
                    <Link
                      href={Routes.transactionDetail({
                        txHash: String(row.l2TxHash),
                        activeTab: 0,
                      })}
                      w={110}
                      isTruncated
                    >
                      {row.l2TxHash}
                    </Link>
                    <CopyToClipboardButton text={row.l2TxHash} />
                  </Flex>
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Link href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}>
                  {row.l2BlockIndex}
                </Link>
              </Table.RowCell>
              <Table.RowCell>
                {row.l1BlockIndex ? (
                  <ExternalLink
                    href={`${env.NEXT_PUBLIC_L1_URL}/block/${row.l1BlockIndex}`}
                    label={String(row.l1BlockIndex)}
                  />
                ) : (
                  <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                )}
              </Table.RowCell>
              <Table.RowCell>
                <InfoPopup
                  title={
                    timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                      ? 'Transaction Datetime:'
                      : 'Transaction Age:'
                  }
                  triggerElement={
                    <Text variant="text3regular" color={COLORS.grey03} whiteSpace="nowrap">
                      {row.timestamp
                        ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </Text>
                  }
                >
                  {row.timestamp
                    ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                    : NOT_APPLICABLE}
                </InfoPopup>
              </Table.RowCell>
              <Table.RowCell>
                <Flex align="center" gap={1}>
                  <Link href={Routes.addressDetail({ hash: String(row.from) })}>
                    {getTruncatedText(row.from, 6, 4)}
                  </Link>
                  <CopyToClipboardButton text={row.from} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <RiArrowRightLine color={COLORS.zircuitPrimary} size={16} />
              </Table.RowCell>
              <Table.RowCell>
                <Flex align="center" gap={1}>
                  <Link href={Routes.addressDetail({ hash: row.to })}>
                    {getTruncatedText(row.to, 6, 4)}
                  </Link>
                  <CopyToClipboardButton text={row.to} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                {row.status === TxItemStatus.failed ? (
                  <InfoPopup
                    title="Txn Status:"
                    triggerElement={
                      <Label
                        variant={TXN_STATUS_VARIANTS[row.status]}
                        icon={TXN_STATUS_ICONS[row.status]}
                      >
                        {row.status}
                      </Label>
                    }
                  >
                    {row.failureReason}
                  </InfoPopup>
                ) : (
                  <Label
                    variant={TXN_STATUS_VARIANTS[row.status]}
                    icon={TXN_STATUS_ICONS[row.status]}
                  >
                    {row.status}
                  </Label>
                )}
              </Table.RowCell>
              <Table.RowCell>
                <Flex align="center" gap={1}>
                  <Link href={Routes.addressDetail({ hash: String(row.confirmedBy) })}>
                    {getTruncatedText(row.confirmedBy, 6, 4)}
                  </Link>
                  <CopyToClipboardButton text={row.confirmedBy} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Flex alignItems="center">
                  <Image
                    alt="Token Asset"
                    {...(row.transfers?.[0]?.symbol &&
                    erc20TokenContractAddresses?.length &&
                    erc20TokenAsset?.type
                      ? {
                          src: getImageSrc({
                            contractAddressL2: erc20TokenAsset?.contract_address_l2,
                            type: erc20TokenAsset?.type,
                          }),
                          boxSize: 4,
                        }
                      : { src: '/assets/ethereum2.svg' })}
                  />
                  <Text variant="text3regular" ml={1}>
                    {getFormattedAsset({ transfers: row.transfers, value: row.value })}
                  </Text>
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <InfoPopup
                  triggerElement={
                    <Text variant="text3regular" color={COLORS.grey03}>
                      {getFormattedNumber(weiToEth(row.fee))} ETH
                    </Text>
                  }
                >
                  <Flex alignItems="center" gap={1}>
                    <Text variant="text3medium" color={COLORS.grey03}>
                      Effective Txn Fee:
                    </Text>
                    <Text variant="text3regular" color={COLORS.grey03}>
                      {getFormattedNumber(weiToEth(row.effectiveFee))} ETH
                    </Text>
                  </Flex>
                </InfoPopup>
              </Table.RowCell>
            </Table.Row>
          )
        }}
      />
    </>
  )
}

TransactionsPage.getLayout = (page: React.ReactNode) => <DetailLayout>{page}</DetailLayout>

export default TransactionsPage
